import { IBasePaginationParams } from "./common";

/**
 * Tình trạng chậm trả — FMS phân loại theo số ngày quá hạn của khoản nợ còn mở cũ nhất,
 * ngưỡng chậm trả / nợ xấu cấu hình theo seller.
 */
export enum DelinquencyStatus {
  Current = "CURRENT",
  Delinquent = "DELINQUENT",
  NonPerforming = "NON_PERFORMING",
}

/** 2 lựa chọn gộp của filter "Khách hàng đặc thù", đứng cạnh các loại ngoại lệ */
export enum SpecialCustomerFilterGroup {
  AllSpecial = "ALL_SPECIAL",
  NotSpecial = "NOT_SPECIAL",
}

/** Loại ngoại lệ tín dụng — UI gọi là "lý do khách hàng đặc thù", danh mục theo seller */
export interface ICreditExceptionType {
  id: number;
  name: string;
  /** false = đã tắt: không gán mới được, khách đang gắn vẫn giữ */
  isActive: boolean;
}

export type SpecialCustomerFilterValue = ICreditExceptionType["id"] | SpecialCustomerFilterGroup;

/** Nhân viên chăm sóc (quản lý nợ) — tìm trong IAM theo SĐT */
export interface ICareStaff {
  iamId: string;
  name: string;
  phoneNumber: string;
}

/** Hồ sơ công nợ 1 khách hàng — đúng shape `CustomerDebtProfile` của staff BFF */
export interface IDebtCustomer {
  /** Khoá kỹ thuật (object_reports.id bên FMS) — id trên path của các API cập nhật, không hiển thị */
  id: number;
  /** Mã đối tượng ở hệ bán hàng */
  objectId: string;
  /** Mã kế toán */
  objectAccId: string;
  name: string;
  /** MST và SĐT luôn có ít nhất 1 trong 2; rỗng khi không có */
  taxCode: string;
  phoneNumber: string;
  taxAddress: string;
  /** Rỗng khi chưa gán nhân viên chăm sóc */
  consultantIamId: string;
  consultantName: string;
  consultantPhone: string;
  /** Hạn mức seller cấp, 0 khi chưa cấp */
  creditLimit: number;
  /** Số ngày công nợ được cấp */
  creditTermDays: number;
  /** Hạn mức đã dùng */
  creditExposure: number;
  /** Hạn mức còn lại; âm nghĩa là khách đã vượt mức được cấp */
  availableCredit: number;
  /** Số ngày quá hạn của khoản nợ còn mở cũ nhất; 0 khi không có khoản quá hạn */
  maxDaysPastDue: number;
  delinquencyStatus: DelinquencyStatus;
  /** Có ngoại lệ ⇒ được xem là khách hàng đặc thù */
  creditExceptionType: Pick<ICreditExceptionType, "id" | "name"> | null;
}

/** Giá trị của form filter trên UI */
export interface IDebtCustomerFilterForm {
  objectAccId?: string;
  taxCode?: string;
  phoneNumber?: string;
  delinquencyStatuses?: DelinquencyStatus[];
  specialCustomerOptions?: SpecialCustomerFilterValue[];
}

/** Query của GET /api/v1/customer-debts */
export interface IGetDebtCustomersParams
  extends Omit<IDebtCustomerFilterForm, "specialCustomerOptions">,
    IBasePaginationParams {
  hasCreditException?: boolean;
  creditExceptionTypeIds?: ICreditExceptionType["id"][];
}

export interface IGetDebtCustomersResponse {
  customerDebts: IDebtCustomer[];
  pagination: Required<IBasePaginationParams> & { total: number };
}

export interface IUpdateCreditLimitPayload {
  id: IDebtCustomer["id"];
  creditLimit: number;
  creditTermDays: number;
}

export interface IUpdateConsultantPayload {
  id: IDebtCustomer["id"];
  consultantIamId: ICareStaff["iamId"];
}

export interface IUpdateCreditExceptionPayload {
  id: IDebtCustomer["id"];
  /** null = gỡ ngoại lệ đang gắn */
  creditExceptionTypeId: ICreditExceptionType["id"] | null;
}
